'use client';

import cx from 'classnames';
import { MapPin, Phone, Search } from 'lucide-react';
import ContactModal from '@/dealership/ContactModal';
import ShiftDigitalEventName from '~/constants/dealership/ShiftDigitalEventName';
import { carvanaInverseLogo } from '~/constants/dealership/testDriveCenter/assets';
import { absoluteBaseUrl } from '~/constants/environment';
import { useModalContext } from '~/context/dealership/ModalContext';
import { useDealershipId } from '~/context/dealership/StaticContext';
import buildDealershipUrl from '~/utilities/dealership/buildDealershipUrl';
import useNavScrollState from '../hooks/useNavScrollState';
import styles from '../TestDriveCenter.module.css';

const section = 'Test Drive Center - Nav';

export default function TestDriveCenterNav() {
  const { isSolid, progress } = useNavScrollState();
  const dealershipId = useDealershipId();
  const { setContactModalOpen } = useModalContext();

  const openContactModal = () => setContactModalOpen(true);

  return (
    <>
      <header
        className={cx(
          styles.nav,
          'fixed inset-x-0 top-0 z-[90] border-b border-transparent bg-transparent',
          { [styles.navSolid]: isSolid }
        )}
      >
        <nav className="mx-auto flex h-[72px] max-w-[1320px] items-center gap-[26px] px-[clamp(20px,4vw,56px)]">
          <img
            alt="Carvana"
            className="block h-28 w-auto"
            src={carvanaInverseLogo}
          />
          <span className="h-20 w-px bg-[rgba(255,255,255,0.28)]" />
          <span className="text-[14.5px] font-bold leading-none tracking-[-0.01em] text-white">
            Test Drive Center
          </span>
          <div className="ml-auto flex items-center gap-[10px]">
            <div className="flex items-center gap-xs max-[920px]:hidden">
              <a
                className="inline-flex cursor-pointer items-center gap-[6px] rounded-full px-[14px] py-[9px] text-[13.5px] font-semibold leading-none text-[rgba(255,255,255,0.82)] hover:text-white"
                data-adobe-id="search-cars"
                data-analytics-id={`${section} - Search cars`}
                data-testid="test-drive-center-nav-search-cars"
                href="/cars"
              >
                <Search size={16} />
                Search cars
              </a>
              <a
                className="inline-flex cursor-pointer items-center gap-[6px] rounded-full px-[14px] py-[9px] text-[13.5px] font-semibold leading-none text-[rgba(255,255,255,0.82)] hover:text-white"
                data-adobe-id="sell-trade"
                data-analytics-id={`${section} - Sell/Trade`}
                data-testid="test-drive-center-nav-sell-trade"
                href={buildDealershipUrl(
                  `${absoluteBaseUrl}/sell-my-car`,
                  dealershipId,
                  'stc'
                )}
                rel="noopener noreferrer"
                target="_blank"
              >
                Sell/Trade
              </a>
              <a
                className="inline-flex cursor-pointer items-center gap-[6px] rounded-full px-[14px] py-[9px] text-[13.5px] font-semibold leading-none text-[rgba(255,255,255,0.82)] hover:text-white"
                data-adobe-id="service"
                data-analytics-id={`${section} - Service`}
                data-sda-event={ShiftDigitalEventName.ScheduleServiceClick}
                data-testid="test-drive-center-nav-service"
                href="/service"
              >
                Service
              </a>
            </div>
            <div className="flex items-center gap-xs">
              <button
                className="inline-flex h-40 w-40 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0 text-white transition-colors duration-200 hover:bg-[rgba(255,255,255,0.12)]"
                data-adobe-id="open-contact-information"
                data-analytics-id={`${section} - Phone Icon`}
                data-testid="test-drive-center-nav-phone"
                onClick={openContactModal}
                type="button"
              >
                <Phone size={22} />
              </button>
              <button
                className="inline-flex h-40 w-40 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0 text-white transition-colors duration-200 hover:bg-[rgba(255,255,255,0.12)]"
                data-adobe-id="open-directions"
                data-analytics-id={`${section} - Directions Icon`}
                data-testid="test-drive-center-nav-directions"
                onClick={openContactModal}
                type="button"
              >
                <MapPin size={22} />
              </button>
            </div>
          </div>
        </nav>
        <div className="absolute -bottom-px left-0 h-[2px] w-full overflow-hidden">
          <div
            className="h-full w-[var(--test-drive-center-progress,0%)] bg-[var(--cvna-blue-bright)]"
            style={
              {
                '--test-drive-center-progress': `${progress}%`
              } as React.CSSProperties
            }
          />
        </div>
      </header>
      <ContactModal />
    </>
  );
}
